import { type Static, Type } from "@sinclair/typebox"
import { Nullable } from "./common"
import { ShardStrategySchema } from "./nodes"

// Ajustes de instancia (GET/PUT /admin/settings) — claves de docs/03 §3 `settings`.
// Editables en caliente; los TTLs van en segundos.

// Alineada a NIST 800-63B: sin reglas de composición ni expiración.
export const PasswordPolicySchema = Type.Object({
  min_length: Type.Integer({ minimum: 8, maximum: 128, default: 12 }),
  max_length: Type.Integer({ minimum: 12, maximum: 128, default: 128 }),
  // Bloqueo de contraseñas comprometidas/comunes [V1]
  check_compromised: Type.Boolean({ default: false }),
})
export type PasswordPolicy = Static<typeof PasswordPolicySchema>

export const TokenTtlsSchema = Type.Object({
  access: Type.Integer({ minimum: 60, default: 900 }),
  refresh: Type.Integer({ minimum: 3600, default: 2_592_000 }),
  m2m: Type.Integer({ minimum: 60, default: 3600 }),
  mfa: Type.Integer({ minimum: 60, default: 300 }),
  reset: Type.Integer({ minimum: 300, default: 3600 }),
  invite: Type.Integer({ minimum: 3600, default: 604_800 }),
})
export type TokenTtls = Static<typeof TokenTtlsSchema>

// Backoff exponencial desde base_lock_seconds hasta max_lock_seconds — docs/06 §1.
export const LockoutPolicySchema = Type.Object({
  max_attempts: Type.Integer({ minimum: 1, default: 5 }),
  base_lock_seconds: Type.Integer({ minimum: 1, default: 60 }),
  max_lock_seconds: Type.Integer({ minimum: 60, default: 3600 }),
})
export type LockoutPolicy = Static<typeof LockoutPolicySchema>

export const ShardSettingsSchema = Type.Object({
  strategy: ShardStrategySchema,
  // Si está apagado y el nodo asignado está down ⇒ 503 node_unavailable
  auto_reassign: Type.Boolean({ default: false }),
})
export type ShardSettings = Static<typeof ShardSettingsSchema>

// `password` es write-only: la API nunca la devuelve en GET (la enmascara).
export const SmtpSettingsSchema = Type.Object({
  host: Type.String(),
  port: Type.Integer({ minimum: 1, maximum: 65535 }),
  secure: Type.Boolean(),
  username: Nullable(Type.String()),
  password: Type.Optional(Type.String()),
  from: Type.String(),
})
export type SmtpSettings = Static<typeof SmtpSettingsSchema>

export const SettingsSchema = Type.Object({
  password_policy: PasswordPolicySchema,
  token_ttls: TokenTtlsSchema,
  lockout_policy: LockoutPolicySchema,
  shard_strategy: ShardSettingsSchema,
  // Orígenes CORS globales; los de cada app key se suman dinámicamente
  cors_origins: Type.Array(Type.String()),
  require_api_key: Type.Boolean(),
  // Habilita la ingesta externa POST /v1/audit/events [V1]
  audit_ingest: Type.Boolean(),
  smtp: Nullable(SmtpSettingsSchema),
  // URL de la app para enlaces de reset/invitación; placeholders {token} y {domain} [V1]
  reset_url_template: Nullable(Type.String()),
})
export type Settings = Static<typeof SettingsSchema>

// PUT parcial por clave: cada clave enviada reemplaza su valor completo.
export const UpdateSettingsRequestSchema = Type.Partial(SettingsSchema)
export type UpdateSettingsRequest = Static<typeof UpdateSettingsRequestSchema>
